import { useQueryClient } from "@tanstack/react-query";
import { UploadCloud } from "lucide-react";
import { useRef, useState } from "react";

import { useBookmarkImportProgressActions } from "@/modules/mine/bookmark-import";
import type { DialogComponentProps } from "@/shared/components/dialog";
import { cn } from "@/shared/lib/utils";
import { Alert, AlertDescription } from "@/shared/ui/alert";
import { Button } from "@/shared/ui/button";
import { DialogFooter } from "@/shared/ui/dialog";
import { Label } from "@/shared/ui/label";

const BOOKMARK_IMPORT_MIME_TYPE = "text/html";

type ImportBookmarksResult = {
  action: "started";
};

function isBookmarkFile(file: File) {
  const lowerName = file.name.toLowerCase();

  return lowerName.endsWith(".html") || lowerName.endsWith(".htm") || file.type === BOOKMARK_IMPORT_MIME_TYPE;
}

export function ImportBookmarks({ close, resolve }: DialogComponentProps<ImportBookmarksResult>) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const { startImport } = useBookmarkImportProgressActions();

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = (nextFile: File | null) => {
    setErrorMessage("");

    if (!nextFile) {
      return;
    }

    if (!isBookmarkFile(nextFile)) {
      setErrorMessage("请选择浏览器导出的 HTML 书签文件。");
      return;
    }

    startImport(nextFile, queryClient);
    resolve({ action: "started" });
  };

  return (
    <>
      <div className="space-y-4">
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">书签文件</Label>
          <button
            type="button"
            className={cn(
              "flex w-full items-center gap-3 rounded-md border bg-background p-3 text-left transition-colors hover:bg-accent/50",
              errorMessage ? "border-destructive" : "border-input",
            )}
            onClick={openFilePicker}
          >
            <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
              <UploadCloud className="size-4" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">
                选择浏览器导出的 HTML 书签文件
              </span>
              <span className="mt-1 block text-xs text-muted-foreground">
                选择后会自动开始导入，你可以继续使用页面。
              </span>
            </span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".html,.htm,text/html"
            className="hidden"
            onChange={(event) => handleFileChange(event.target.files?.[0] ?? null)}
          />
        </div>

        {errorMessage ? (
          <Alert variant="destructive">
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        ) : null}
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={close}>
          取消
        </Button>
        <Button type="button" onClick={openFilePicker}>
          选择文件
        </Button>
      </DialogFooter>
    </>
  );
}
